import { faFileExcel } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQueryClient } from '@tanstack/react-query';
import { ChangeEvent, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import * as XLSX from 'xlsx';

import { InlineMessage } from 'src/common/components/InlineMessage';
import { Modal } from 'src/common/components/Modal';
import { Button } from 'src/common/components/buttons/Button';
import { updateTechCrew } from 'src/project/api/techCrewsApi';
import { TechCrew, WorkerEntry } from 'src/project/types/techCrews';

const EXCEL_FILE_EXTENSIONS = '.xlsx,.xls';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  techCrew: TechCrew;
};

export const ImportWorkersModal = ({ isOpen, onClose, techCrew }: Props) => {
  const { t } = useTranslation(['project', 'common']);
  const { projectId = '' } = useParams();
  const queryClient = useQueryClient();
  const fileEl = useRef<HTMLInputElement>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<WorkerEntry[] | null>(null);

  const openFileDialog = () => {
    fileEl.current?.click();
  };

  const resetState = () => {
    setError(null);
    setPreview(null);
    if (fileEl.current) fileEl.current.value = '';
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setPreview(null);
    const selectedFile = e.currentTarget.files?.[0];
    if (!selectedFile) return;

    const fileExtension = selectedFile.name
      .substring(selectedFile.name.lastIndexOf('.'))
      .toLowerCase();

    if (fileExtension !== '.xlsx' && fileExtension !== '.xls') {
      setError(t('project:techCrewEdit.workersView.importModal.invalidFile'));
      if (fileEl.current) fileEl.current.value = '';
      return;
    }

    setIsParsing(true);
    try {
      const data = await selectedFile.arrayBuffer();
      const workbook = XLSX.read(data);
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json<string[]>(firstSheet, {
        header: 1,
      });

      // Skip header row, filter empty rows, take first column only
      const names = rows
        .slice(1)
        .map((row) => String(row[0] ?? '').trim())
        .filter((name) => name.length > 0);

      if (names.length === 0) {
        setError(t('project:techCrewEdit.workersView.importModal.emptyFile'));
        return;
      }

      setPreview(names.map((name) => ({ name })));
    } catch {
      setError(t('project:techCrewEdit.workersView.importModal.parseError'));
    } finally {
      setIsParsing(false);
    }
  };

  const handleConfirm = async () => {
    if (!preview) return;
    setIsSaving(true);
    try {
      await updateTechCrew(projectId, techCrew._id, { workers: preview });
      await queryClient.invalidateQueries({
        queryKey: ['projects', projectId],
      });
      toast.success(
        t('project:techCrewEdit.workersView.importModal.successToast'),
      );
      resetState();
      onClose();
    } catch {
      toast.error(t('project:techCrewEdit.workersView.importModal.errorToast'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={handleClose} className="w-[520px]">
      <Modal.Header onClose={handleClose}>
        {t('project:techCrewEdit.workersView.importModal.title')}
      </Modal.Header>
      <Modal.Body>
        <input
          ref={fileEl}
          type="file"
          accept={EXCEL_FILE_EXTENSIONS}
          onChange={handleFileChange}
          className="sr-only"
        />

        {!preview ? (
          <>
            <p className="text-shuttleGray-600">
              {t('project:techCrewEdit.workersView.importModal.description')}
            </p>
            {error && (
              <InlineMessage variant="error" className="mt-4">
                {error}
              </InlineMessage>
            )}
            <div className="mt-6 text-center">
              <Button
                onClick={openFileDialog}
                disabled={isParsing}
                loading={isParsing}
              >
                <FontAwesomeIcon icon={faFileExcel} />
                {t('common:button.selectFile')}
              </Button>
            </div>
          </>
        ) : (
          <>
            <InlineMessage variant="warning" className="mb-4">
              {t('project:techCrewEdit.workersView.importModal.replaceWarning')}
            </InlineMessage>
            <p className="mb-3 font-semibold text-shuttleGray-800">
              {t('project:techCrewEdit.workersView.importModal.previewCount', {
                count: preview.length,
              })}
            </p>
            <ul className="max-h-64 divide-y divide-gray-100 overflow-y-auto rounded border border-gray-200">
              {preview.map((worker, i) => (
                <li key={i} className="px-3 py-2 text-sm">
                  {worker.name}
                </li>
              ))}
            </ul>
            <div className="mt-6 flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={resetState}
                disabled={isSaving}
              >
                {t('common:button.back')}
              </Button>
              <Button
                onClick={handleConfirm}
                loading={isSaving}
                disabled={isSaving}
              >
                {t(
                  'project:techCrewEdit.workersView.importModal.confirmButton',
                )}
              </Button>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};
